import { deskCard, deskMuted, deskPrimaryBtn, deskSecondaryBtn } from "./salesDeskStyles";
import type { SalesDeskOrder } from "./salesDeskOrderFilter";
import {
  salesDeskRecordHistory,
  type SalesDeskHistoryEntry,
} from "./salesDeskSaleRecord";

function EmptyPhoto() {
  return (
    <div className="flex min-h-[220px] items-center justify-center rounded-[13px] bg-[#e7eef2] text-sm text-[#687772] dark:bg-zinc-800 dark:text-zinc-400">
      No photo
    </div>
  );
}

function StatusTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[13px] bg-[#eef2f4] px-4 py-3 dark:bg-zinc-800">
      <p className="text-xs text-[#687772] dark:text-zinc-400">{label}</p>
      <p className="mt-1 font-semibold">{value}</p>
    </div>
  );
}

export function SalesDeskRecord({
  sale,
  notes,
  onMatches,
  onOrderAnother,
  onHandoff,
}: {
  sale: SalesDeskOrder | null;
  notes: SalesDeskHistoryEntry[];
  onMatches: () => void;
  onOrderAnother: () => void;
  onHandoff: (action: "correction" | "approve") => void;
}) {
  if (!sale) {
    return <p className={deskMuted}>This sale is not on the list.</p>;
  }
  const history = [...notes, ...salesDeskRecordHistory(sale.createdAt, sale.updatedAt, sale.eventHint)];
  const onLot = Boolean(sale.serial);

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#687772] dark:text-zinc-400">{sale.id.slice(0, 8)}</p>
      <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px]">{sale.customer}</h1>
      <p className={`mt-2 ${deskMuted}`}>{sale.model.trim() || "Model not on this sale"}</p>
      {sale.serial ? <p className="mt-1 break-all text-xs text-[#687772] dark:text-zinc-400">{sale.serial}</p> : null}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className={deskCard}>
          <EmptyPhoto />
          <div className="mt-4 grid grid-cols-1 gap-3">
            <StatusTile label="Sale status" value={sale.saleStatus} />
            <StatusTile label="Fulfillment" value={sale.fulfillment} />
          </div>
          <h2 className="mt-6 text-[21px] font-semibold">Customer actions</h2>
          <p className="mt-2 text-sm">
            Acceptance: <strong>Not recorded</strong>
            <br />
            Deposit: <strong>Not recorded</strong>
            <br />
            RTO application: <strong>Not recorded</strong>
          </p>
          {onLot ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" onClick={onMatches} className={deskSecondaryBtn}>
                Matching sales history
              </button>
              <button type="button" onClick={onOrderAnother} className={deskPrimaryBtn}>
                Order another for stock
              </button>
            </div>
          ) : null}
        </section>
        <section className={deskCard}>
          <h2 className="text-[21px] font-semibold">Administrator handoff</h2>
          <p className={`mt-3 ${deskMuted}`}>The administrator reviews this sale outside this desk.</p>
          <p className="mt-4 text-sm">Check selected options, paperwork, identity and delivery details.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => onHandoff("correction")} className={deskSecondaryBtn}>
              Request correction
            </button>
            <button type="button" onClick={() => onHandoff("approve")} className={deskPrimaryBtn}>
              Approve
            </button>
          </div>
          <h3 className="mt-6 text-[18px] font-semibold">Record history</h3>
          {history.length === 0 ? <p className={`mt-3 ${deskMuted}`}>No history on this sale yet.</p> : null}
          <div className="mt-3 grid gap-2">
            {history.map((entry, index) => (
              <div key={`${entry.at}-${entry.text}-${index}`} className="border-l-2 border-[#d5e0ea] bg-[#eef2f4] px-3 py-2 dark:border-zinc-600 dark:bg-zinc-800">
                <p className="text-xs text-[#687772] dark:text-zinc-400">{entry.at}</p>
                <p className="text-sm">{entry.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
